import React from 'react';
import moment from 'moment-timezone';
import { isNil } from 'lodash';
import {
  formatDate,
  formatBirthDate,
  formatDayRemainings,
  formatPrimaryClinician,
  formatTosPhiDisclosure,
} from './formatter';
import { RowCell } from './RowCell';
import LessonCompletionCell from './LessonCompletionCell';
import './styles.scss';

const DEFAULT_EMPTY_VALUE = '-';

interface IProps {
  row: IFormattedPatientType;
  headerKey: string;
}

const PatientAdherenceRowCell = (props: IProps) => {
  const { row, headerKey } = props;
  const isWeekProp = headerKey.includes('week');
  const propName = isWeekProp ? 'week' : headerKey;

  switch (propName) {
    case 'last_name':
      return <RowCell sticky>{row.lastName}</RowCell>;
    case 'first_name':
      return <RowCell sticky>{row.firstName}</RowCell>;
    case 'birth_date':
      return <RowCell>{formatBirthDate(row.birthDate)}</RowCell>;
    case 'email':
      return <RowCell>{row.email}</RowCell>;
    case 'clinic':
      return <RowCell>{row.clinicName}</RowCell>;
    case 'health_care_provider':
      return (
        <RowCell>{formatPrimaryClinician(row.healthCareProvider)}</RowCell>
      );
    case 'external_ref':
      return (
        <RowCell>
          {!row.externalRef ? DEFAULT_EMPTY_VALUE : row.externalRef}
        </RowCell>
      );
    case 'rx_created_date':
      return <RowCell>{formatDate(row.rxCreatedDate, row.rxTimezone)}</RowCell>;
    case 'rx_start_date':
      return (
        <RowCell>
          {row.rxStartDate
            ? formatDate(row.rxStartDate, row.rxTimezone)
            : row.accessCode}
        </RowCell>
      );
    case 'tos_diphi':
      return (
        <RowCell>
          {formatTosPhiDisclosure(row.phiDisclosure, row.tosVersionAccepted)}
        </RowCell>
      );
    case 'rx_days_remaining':
      return (
        <RowCell>
          {isNil(row.rxDaysRemaining)
            ? DEFAULT_EMPTY_VALUE
            : formatDayRemainings(row.rxDaysRemaining)}
        </RowCell>
      );
    case 'week': {
      const weekIndex = parseInt(headerKey.split('_')[1], 10) - 1;
      const numEvents = row.lessonsPerWeek ? row.lessonsPerWeek[weekIndex] : 0;
      const currentWeekIndex = row.rxStartDate
        ? moment().diff(moment(row.rxStartDate), 'weeks')
        : -1;
      return (
        <LessonCompletionCell
          key={`${row.key}-${weekIndex}`}
          weekIndex={weekIndex}
          numCompletions={numEvents}
          currentWeekIndex={currentWeekIndex}
          prescriptionIsNotStarted={!row.rxStartDate}
        />
      );
    }
    case 'total_lessons_completed':
      return <RowCell>{row.totalLessonsCompleted}</RowCell>;
    case 'days_since_clinic_visit':
      return (
        <RowCell>
          {isNil(row.daysSinceClinicVisit)
            ? DEFAULT_EMPTY_VALUE
            : row.daysSinceClinicVisit}
        </RowCell>
      );
    case 'rewards_earned_last_7_days':
      return (
        <RowCell>
          {isNil(row.rewardsEarnedLast7Days)
            ? DEFAULT_EMPTY_VALUE
            : row.rewardsEarnedLast7Days}
        </RowCell>
      );
    case 'rewards_earned_total':
      return (
        <RowCell>
          {isNil(row.rewardsEarnedTotal)
            ? DEFAULT_EMPTY_VALUE
            : row.rewardsEarnedTotal}
        </RowCell>
      );
    default:
      return null;
  }
};

export default PatientAdherenceRowCell;
